import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  ChevronRight,
  Calendar,
  Save,
  RotateCcw,
  Shield,
  Users,
  Key,
  Settings,
  CheckSquare
} from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import roleService from '../../services/roleService'
import { toast } from 'react-toastify'
// Consolidated CSS is imported globally'

const RoleMasterEdit = () => {
  const navigate = useNavigate()
  const location = useLocation()
  
  // Get the role data from navigation state or URL params
  const roleData = location.state?.roleData || {
    id: 1,
    roleCode: 'R001',
    roleName: 'Super Admin',
    displayRoleName: 'Super Admin',
    description: 'Full system access with all permissions',
    hierarchy: 1,
    status: 'Active',
    permissions: ['dashboard', 'sales_view', 'sales_create', 'sales_edit', 'sales_delete', 'crm_view', 'crm_create', 'crm_edit', 'contracts_view', 'contracts_create', 'projects_view', 'projects_create', 'timesheets_view', 'timesheets_create', 'expenses_view', 'expenses_create', 'procurement_view', 'procurement_create', 'subscriptions_view', 'assets_view', 'billing_view', 'settings_view', 'settings_edit', 'user_management', 'role_management'],
    createdDate: '2024-01-01',
    redirectTo: './default/sales/home'
  }

  const [formData, setFormData] = useState({
    roleCode: '',
    roleName: '',
    displayRoleName: '',
    description: '',
    hierarchy: 1,
    status: 'Active',
    permissions: [],
    redirectTo: './default/sales/home',
    createdDate: '',
    lastModified: new Date().toISOString().split('T')[0]
  })
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)

  // Available permissions list
  const availablePermissions = [
    { id: 'dashboard', name: 'Dashboard Access', category: 'General' },
    { id: 'sales_view', name: 'View Sales', category: 'Sales' },
    { id: 'sales_create', name: 'Create Sales', category: 'Sales' },
    { id: 'sales_edit', name: 'Edit Sales', category: 'Sales' },
    { id: 'sales_delete', name: 'Delete Sales', category: 'Sales' },
    { id: 'crm_view', name: 'View CRM', category: 'CRM' },
    { id: 'crm_create', name: 'Create CRM', category: 'CRM' },
    { id: 'crm_edit', name: 'Edit CRM', category: 'CRM' },
    { id: 'contracts_view', name: 'View Contracts', category: 'Contracts' },
    { id: 'contracts_create', name: 'Create Contracts', category: 'Contracts' },
    { id: 'projects_view', name: 'View Projects', category: 'Projects' },
    { id: 'projects_create', name: 'Create Projects', category: 'Projects' },
    { id: 'timesheets_view', name: 'View Timesheets', category: 'Timesheets' },
    { id: 'timesheets_create', name: 'Create Timesheets', category: 'Timesheets' },
    { id: 'expenses_view', name: 'View Expenses', category: 'Expenses' },
    { id: 'expenses_create', name: 'Create Expenses', category: 'Expenses' },
    { id: 'procurement_view', name: 'View Procurement', category: 'Procurement' },
    { id: 'procurement_create', name: 'Create Procurement', category: 'Procurement' },
    { id: 'subscriptions_view', name: 'View Subscriptions', category: 'Subscriptions' },
    { id: 'assets_view', name: 'View Assets', category: 'Assets' },
    { id: 'billing_view', name: 'View Billing', category: 'Billing' },
    { id: 'settings_view', name: 'View Settings', category: 'Settings' },
    { id: 'settings_edit', name: 'Edit Settings', category: 'Settings' },
    { id: 'user_management', name: 'User Management', category: 'Security' },
    { id: 'role_management', name: 'Role Management', category: 'Security' }
  ]

  // Pre-fill form data when component mounts
  useEffect(() => {
    const loadRoleData = async () => {
      try {
        setInitialLoading(true)
        
        if (roleData.id) {
          // Fetch fresh data from backend
          const response = await roleService.getRoleById(roleData.id)
          if (response.success) {
            const role = roleService.transformRoleForDisplay(response.data)
            setFormData({
              id: role.id,
              roleCode: role.roleCode || '',
              roleName: role.roleName || '',
              displayRoleName: role.displayRoleName || '',
              description: role.description || '',
              hierarchy: role.hierarchy || 1,
              status: role.status || 'Active',
              permissions: role.permissions || [],
              redirectTo: role.redirectTo || './default/sales/home',
              createdDate: role.createdDate || '',
              lastModified: role.lastModified || new Date().toISOString().split('T')[0]
            })
          }
        } else {
          // Use passed data if no ID
          setFormData({
            id: roleData.id,
            roleCode: roleData.roleCode || '',
            roleName: roleData.roleName || '',
            displayRoleName: roleData.displayRoleName || '',
            description: roleData.description || '',
            hierarchy: roleData.hierarchy || 1,
            status: roleData.status || 'Active',
            permissions: roleData.permissions || [],
            redirectTo: roleData.redirectTo || './default/sales/home',
            createdDate: roleData.createdDate || '',
            lastModified: roleData.lastModified || new Date().toISOString().split('T')[0]
          })
        }
        } catch (err) {
          console.error('Failed to load role data:', err.message)
        // Fallback to passed data
        setFormData({
          id: roleData.id,
          roleCode: roleData.roleCode || '',
          roleName: roleData.roleName || '',
          displayRoleName: roleData.displayRoleName || '',
          description: roleData.description || '',
          hierarchy: roleData.hierarchy || 1,
          status: roleData.status || 'Active',
          permissions: roleData.permissions || [],
          redirectTo: roleData.redirectTo || './default/sales/home',
          createdDate: roleData.createdDate || '',
          lastModified: roleData.lastModified || new Date().toISOString().split('T')[0]
        })
      } finally {
        setInitialLoading(false)
      }
    }

    loadRoleData()
  }, [roleData.id])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handlePermissionChange = (permissionId, isChecked) => {
    setFormData(prev => ({
      ...prev,
      permissions: isChecked 
        ? [...prev.permissions, permissionId]
        : prev.permissions.filter(id => id !== permissionId)
    }))
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      
      // Validate required fields
      if (!formData.roleCode || !formData.roleName) {
        error('Please fill in all required fields')
        return
      }

      // Transform data for backend
      const roleData = roleService.transformRoleForSubmission(formData)
      
      // Update role using the ID from formData
      const response = await roleService.updateRole(formData.id, roleData)
      
      console.log('Update response:', response) // Debug log
      
      if (response.success) {
        toast.success('Role updated successfully!', { autoClose: 2000 })
        navigate('/settings/role-master')
        } else {
          console.error('Failed to update role:', response.message)
        }
      } catch (err) {
        console.error('Failed to update role:', err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      roleCode: roleData.roleCode || '',
      roleName: roleData.roleName || '',
      description: roleData.description || '',
      hierarchy: roleData.hierarchy || 1,
      status: roleData.status || 'Active',
      permissions: roleData.permissions || [],
      createdDate: roleData.createdDate || new Date().toISOString().split('T')[0]
    })
  }

  const handleBack = () => {
    navigate('/settings/role-master')
  }

  // Group permissions by category
  const groupedPermissions = availablePermissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = []
    }
    acc[permission.category].push(permission)
    return acc
  }, {})

  return (
    <div className="page-container">
      <div className="page-content">
        {/* Breadcrumb Navigation */}
        <div className="breadcrumb-section">
          <nav className="breadcrumb-nav">
            <button className="breadcrumb-item" onClick={() => navigate('/settings')}>
              <span>Settings</span>
            </button>
            <ChevronRight size={14} className="breadcrumb-separator" />
            <button className="breadcrumb-item" onClick={() => navigate('/settings?tab=masters')}>
              <span>Masters</span>
            </button>
            <ChevronRight size={14} className="breadcrumb-separator" />
            <span className="breadcrumb-current">Role Master</span>
          </nav>
        </div>

        {/* Header Section */}
        <div className="page-header">
          <div className="header-left">
            <button className="back-btn" onClick={handleBack}>
              <ArrowLeft size={20} />
            </button>
            <div className="page-title">
              <h1>Role Master - Edit</h1>
              <p>Edit user role and permissions</p>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="card">
          <div className="form-header-dark">
            <h2>Edit Role Master</h2>
          </div>
          <div className="card-body">
            <form className="form-content">
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="roleCode" className="form-label required">
                    Role Code
                  </label>
                  <input
                    type="text"
                    id="roleCode"
                    name="roleCode"
                    value={formData.roleCode}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter role code"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="roleName" className="form-label required">
                    Role Name
                  </label>
                  <input
                    type="text"
                    id="roleName"
                    name="roleName"
                    value={formData.roleName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter role name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="displayRoleName" className="form-label">
                    Display Role Name
                  </label>
                  <input
                    type="text"
                    id="displayRoleName"
                    name="displayRoleName"
                    value={formData.displayRoleName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter display role name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="hierarchy" className="form-label required">
                    Hierarchy Level
                  </label>
                  <select
                    id="hierarchy"
                    name="hierarchy"
                    value={formData.hierarchy}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value={1}>1 - Highest</option>
                    <option value={2}>2 - High</option>
                    <option value={3}>3 - Medium</option>
                    <option value={4}>4 - Low</option>
                    <option value={5}>5 - Lowest</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="status" className="form-label required">
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="createdDate" className="form-label required">
                    Created Date
                  </label>
                  <div className="date-input-wrapper">
                    <input
                      type="date"
                      id="createdDate"
                      name="createdDate"
                      value={formData.createdDate}
                      onChange={handleInputChange}
                      className="form-input date-input"
                    />
                    <Calendar size={16} className="calendar-icon" />
                  </div>
                </div>
              </div>

              {/* Permissions Section */}
              <div className="form-group">
                <label className="form-label required">
                  Permissions
                </label>
                <div className="permissions-container">
                  {Object.entries(groupedPermissions).map(([category, permissions]) => (
                    <div key={category} className="permission-category">
                      <h4 className="permission-category-title">{category}</h4>
                      <div className="permission-list">
                        {permissions.map(permission => (
                          <label key={permission.id} className="permission-item">
                            <input
                              type="checkbox"
                              checked={formData.permissions.includes(permission.id)}
                              onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                              className="permission-checkbox"
                            />
                            <span className="permission-name">{permission.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn-secondary"
                >
                  <RotateCcw size={16} />
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="btn-primary"
                  disabled={loading}
                >
                  <Save size={16} />
                  {loading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RoleMasterEdit
